import { index, modelOptions, mongoose, post, prop, PropType, Ref, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { BASE_WARNING_VARIANT, standardWarningDescriptions } from "../../Configurations/WarningConfigurations.js";
import { translationParamsZodValidator } from "../commonZodValidators/translationParams.js";
import { WarningLevel, WarningType } from "../../WarningDescription.js";
import {
    getZodSchemaFromClass,
    mongooseValidatorFromZod,
    ObjectIdString,
    validate,
    WarningLevelString,
    WarningTypeString,
} from "../../zod-decorator/index.js";
import type { AreaOfInterest } from "../areaOfInterest/areaOfInterest.js";
import { ForeignLookup } from "../commonSubSchemas/foreignLookup.js";
import type { Organization } from "../organization/organization.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { WarningFieldsWithDefaults, WarningSchemaTypes } from "../types/warning.d.js";
import { trackMixed } from "../utils/mixedSchemaSavingUtils.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { WarningFixEntry } from "./subSchemas/warningFixEntry.js";
import { WarningProblemEntry } from "./subSchemas/warningProblemEntry.js";
import * as utilities from "./utilities.js";

const SCHEMA_KEY = "warnings" as const;

/**
 * Use this when a warning applies to the whole object rather than a specific sensor or subsystem.
 *
 * Source keys follow a dot-separated namespace path. `root` is the top-level segment. For warnings
 * scoped to a specific subsystem, extend the path — e.g. `root.sensor.0.watermark` for index-0
 * watermark sensor warnings. The helper `getWarningSourceKey(sensorType, sensorIndex)` in the backend
 * produces correctly composed sensor keys.
 */
export const ROOT_WARNING_SOURCE_KEY = "root" as const;

// Mongoose's DocumentArray cast passes `null` entries through untouched and never
// runs the subschema validators on them, so entry lists reject non-object entries here.
const entriesMustBeObjects = mongooseValidatorFromZod(z.array(z.looseObject({})));

@index(
    { "foreignLookup.targetId": 1, "foreignLookup.targetCollection": 1, warningType: 1, warningSourceKey: 1 },
    { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } },
)
// Supports scoping active warnings by area of interest (e.g. the notification pipeline
// resolving which warnings fall under a watched AOI) without joining back to the device.
@index({ aoi: 1, versionIsCurrent: 1, deleted: 1 })
// Supports the notification pipeline's `asOfT` lookup: the most-recent version of a record
// with `versionValidFrom <= T` (the state a phone last saw), keyed by episode (`recordId`).
@index({ recordId: 1, versionValidFrom: 1 })
// Support the notification pipeline's delta scan: current versions in a scope changed since a
// floor. `deleted` is deliberately not in the key — the scan matches active OR resolved, and
// `versionValidFrom` must follow the equality prefix directly to serve as a range bound.
@index({ aoi: 1, versionIsCurrent: 1, versionValidFrom: 1 })
@index({ owner: 1, versionIsCurrent: 1, versionValidFrom: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "warnings" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<Warning>>(...addSchemaKeyHook(SCHEMA_KEY))
export class Warning extends ScdDocument<Warning, WarningFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ childClass: ForeignLookup, responseOnly: true })
    @prop({ type: () => ForeignLookup, required: true, _id: false })
    public foreignLookup!: ForeignLookup;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    /**
     * The area of interest the warning's target belonged to when the warning was last posted.
     * Denormalized from the target device (`device._aoi`) so the warning can be filtered by AOI
     * without a join. Optional: targets with no AOI assignment (or target types that have no AOI
     * concept, e.g. Sensoterra devices) leave it unset. Carried forward and refreshed on each
     * re-post like `owner`, so a device moving between AOIs updates its active warnings.
     */
    @validate({ zodValidator: ObjectIdString.optional(), responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "AreaOfInterest" })
    public aoi?: Ref<AreaOfInterest>;

    @validate({ zodValidator: WarningTypeString, responseOnly: true })
    @prop({
        type: () => String,
        required: true,
        validate: {
            validator: function (this, value): boolean {
                // hasOwnProperty, not a bare index read: the latter walks the prototype chain, so
                // "toString" would resolve to a function and pass as a configured type.
                return Object.prototype.hasOwnProperty.call(standardWarningDescriptions, value);
            },
            message: (props: { value: string }) =>
                `Unknown warningType "${props.value}": no configuration found in standardWarningDescriptions.`,
        },
    })
    public warningType!: WarningType;

    /**
     * Identifies the subsystem this warning belongs to, so that a healthy subsystem cannot
     * accidentally clear a warning that belongs to a different one.
     *
     * Keys are dot-separated namespace paths:
     * - `root` — warning applies to the whole entity (use `ROOT_WARNING_SOURCE_KEY`)
     * - `root.sensor.<index>.<sensorType>` — warning scoped to a specific sensor slot,
     *   e.g. `root.sensor.0.watermark` for the index-0 watermark sensor.
     *
     * The `(foreignLookup, warningType, warningSourceKey)` triple is the unique identity of an
     * active warning. Two handlers posting the same `warningType` with different source keys
     * produce independent warning documents and clear independently.
     *
     * On the backend you can use `getWarningSourceKey(sensorType, sensorIndex)` from
     * `warningHandlers/sensorReadingWarningHandler/checks.ts` to produce sensor keys consistently.
     */
    @validate({ zodValidator: z.string().min(1), responseOnly: true })
    @prop({ type: () => String, required: true })
    public warningSourceKey!: string;

    @validate({ zodValidator: z.string().min(1), responseOnly: true })
    @prop({ type: () => String, required: true })
    public warningTitle!: string;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public warningDetails?: string;

    /**
     * Identifies which specific sub-form of this warning type is active. Required on every
     * warning. Defaults to `BASE_WARNING_VARIANT` so warning documents written before this
     * field existed continue to validate when re-hydrated and re-saved.
     *
     * Accepted values: `BASE_WARNING_VARIANT` (always) OR any value listed in
     * `standardWarningDescriptions[warningType].variants`. Handlers must always supply
     * the field explicitly — the default is a backwards-compat safety net, not a
     * convenience for handler authors.
     *
     * If `warningType` itself is unknown (i.e. not in `standardWarningDescriptions`), variant
     * validation is skipped entirely — the `warningType` field's own validator is the sole
     * reporter for that error, preventing a confusing secondary failure on the variant.
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({
        type: () => String,
        required: true,
        default: BASE_WARNING_VARIANT,
        validate: {
            validator: function (this: Warning, value: string): boolean {
                if (value === BASE_WARNING_VARIANT) return true;
                const config = standardWarningDescriptions[this.warningType];
                // If warningType is unknown, skip — the warningType field's own validator
                // is the sole reporter for that error.
                if (!config) return true;
                if ((config.variants as readonly string[]).includes(value)) return true;
                const accepted = [BASE_WARNING_VARIANT, ...(config.variants as readonly string[])]
                    .map((v) => `"${v}"`)
                    .join(", ");
                throw new Error(
                    `Warning variant "${value}" is not valid for warningType "${this.warningType}". Accepted: [${accepted}].`,
                );
            },
            message: (props: { value: string; reason?: { message?: string } }): string => {
                if (props.reason?.message) return props.reason.message;
                return `Warning variant "${props.value}" is not valid for this warningType.`;
            },
        },
    })
    public warningVariant: string = BASE_WARNING_VARIANT;

    /**
     * Per-instance values interpolated into the warning's translation templates.
     * Param names that collide with i18next reserved options (e.g. `count`,
     * `defaultValue`) are rejected so renderers can never silently mis-resolve.
     */
    @validate({ zodValidator: translationParamsZodValidator().optional(), responseOnly: true })
    @trackMixed
    @prop({
        type: () => mongoose.Schema.Types.Mixed,
        required: false,
        allowMixed: Severity.ALLOW,
        validate: mongooseValidatorFromZod(translationParamsZodValidator()),
    })
    public warningParams?: Record<string, string | number>;

    /**
     * Structured "potential causes" attached to this warning instance. Each entry's
     * `problemKey` must be listed in `standardWarningDescriptions[warningType].possibleProblemKeys`
     * and must have a matching catalog string under `<warningType>.problems.<problemKey>`.
     * `problemParams` are interpolation values for the problem's template, subject to
     * the same i18next reserved-key rules as `warningParams`.
     *
     * Rendered by the dashboard as a "Possible causes" list under the description.
     * Backwards compatible: documents written before this field existed simply omit it.
     */
    @validate({
        childClass: WarningProblemEntry,
        childClassTransform: (object) => z.array(object).optional(),
        responseOnly: true,
    })
    @prop(
        { type: () => [WarningProblemEntry], required: false, _id: false, validate: entriesMustBeObjects },
        PropType.ARRAY,
    )
    public warningProblems?: WarningProblemEntry[];

    /**
     * Structured "suggested fixes" attached to this warning instance. Each entry's
     * `fixKey` must be listed in `standardWarningDescriptions[warningType].possibleFixKeys`
     * and must have a matching catalog string under `<warningType>.fixes.<fixKey>`.
     * `fixParams` are interpolation values for the fix's template, subject to the same
     * i18next reserved-key rules as `warningParams`.
     *
     * Rendered by the dashboard as a "Potential fixes" list. The handler selects which
     * of the type's available fixes apply. Backwards compatible: documents written
     * before this field existed simply omit it and fall back to the config `simpleFix`.
     */
    @validate({
        childClass: WarningFixEntry,
        childClassTransform: (object) => z.array(object).optional(),
        responseOnly: true,
    })
    @prop(
        { type: () => [WarningFixEntry], required: false, _id: false, validate: entriesMustBeObjects },
        PropType.ARRAY,
    )
    public warningFixes?: WarningFixEntry[];

    /**
     * The severity level of this warning instance.
     *
     * See `WarningLevel` for the possible values:
     * - `WarningLevel.INFO` (0) — informational only
     * - `WarningLevel.WARNING` (1) — potential issue requiring attention
     * - `WarningLevel.ERROR` (2) — definite problem requiring action
     *
     * Must be one of the levels listed in
     * `WarningConfig.standardWarningDescriptions[warningType].possibleWarningLevels`.
     * Saving a document whose `warningLevel` is not compatible with its `warningType` will fail
     * with a Mongoose `ValidationError`.
     */
    @validate({ zodValidator: WarningLevelString, responseOnly: true })
    @prop({
        type: () => Number,
        required: true,
        validate: {
            validator: function (this: Warning, value: WarningLevel): boolean {
                const config = standardWarningDescriptions[this.warningType];
                // If warningType is unknown, skip validation here — the warningType field's own
                // validator is the sole reporter for that error, preventing a confusing secondary failure.
                if (!config) return true;

                const possibleWarningLevels = config.possibleWarningLevels as readonly WarningLevel[];
                if (possibleWarningLevels.includes(value)) return true;

                const levelName = WarningLevel[value] ?? String(value);
                const acceptedLevels = possibleWarningLevels
                    .map((level) => `"${WarningLevel[level] ?? String(level)}" (${level})`)
                    .join(", ");

                throw new Error(
                    `Warning level "${levelName}" (${value}) is invalid for warning type "${this.warningType}". Accepted levels: [${acceptedLevels}].`,
                );
            },
            message: (props: { value: WarningLevel; reason?: { message?: string } }): string => {
                if (props.reason?.message) return props.reason.message;
                const levelName = WarningLevel[props.value] ?? String(props.value);
                return `Warning level "${levelName}" (${props.value}) is not in the possibleWarningLevels for this warningType.`;
            },
        },
    })
    public warningLevel!: WarningLevel;

    /**
     * Priority of warnings of this type. Warnings that are "causes" have a high priority, while warnings that
     * are "effects" have low priority.
     */
    @validate({ zodValidator: z.number().int().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 1 })
    public warningTypePriority: number = 1;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public seen: boolean = false;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public SMSSent: boolean = false;

    static getters = {} as const satisfies Record<string, (self: Warning) => any>;

    static utilities = { ...utilities } as const;

    static dtoValidators: DtoValidatorTypes<WarningSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<Warning, WarningFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

Warning.dtoValidators = getZodSchemaFromClass(Warning) as unknown as DtoValidatorTypes<WarningSchemaTypes>;
